/**
 * The one place that knows how to reach the API.
 *
 * Requests go to the same-origin `/api` prefix: the Vite dev server proxies it and the
 * production reverse proxy routes it. Types come from `schema.d.ts`, generated from the
 * running API by `make web-api-types`, so a contract change is a type error here.
 */
import type { components } from "@/api/schema";

export type Document = components["schemas"]["DocumentOut"];
export type DocumentStatus = Document["status"];
export type Page = components["schemas"]["PageOut"];
export type PageView = components["schemas"]["ViewOut"];
export type Pages = components["schemas"]["PagesOut"];
export type Run = components["schemas"]["RunOut"];
export type RunCreate = components["schemas"]["RunCreate"];
export type RunStatus = Run["status"];
export type RunMode = NonNullable<RunCreate["mode"]>;

export const API_PREFIX = "/api";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`${status}: ${detail}`);
    this.name = "ApiError";
  }
}

async function detailOf(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") {
      return body.detail;
    }
    if (Array.isArray(body.detail)) {
      // FastAPI validation errors: a list of {loc, msg, type}.
      return body.detail
        .map((item: { msg?: unknown }) => (typeof item.msg === "string" ? item.msg : ""))
        .filter(Boolean)
        .join("; ");
    }
  } catch {
    // Not JSON; fall through to the status text.
  }
  return response.statusText || `request failed with ${response.status}`;
}

let unauthorized: (() => void) | null = null;

/** Who hears about a 401: sign-in sends the user to its page. `null` stops listening. */
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorized = handler;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * Every call to the API. Sends the session cookie, and marks a mutating request with
 * `X-Requested-With`, which a cross-site form cannot set (the CSRF defence of ADR-0012).
 * A failure becomes an `ApiError`; a 401 is also reported to `onUnauthorized`.
 */
async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (!SAFE_METHODS.has(method)) {
    headers.set("X-Requested-With", "uto");
  }
  const response = await fetch(`${API_PREFIX}${path}`, {
    ...init,
    method,
    headers,
    credentials: "same-origin",
  });
  if (!response.ok) {
    if (response.status === 401) {
      unauthorized?.();
    }
    throw new ApiError(response.status, await detailOf(response));
  }
  return response;
}

/** A JSON request and its JSON response. */
async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  const response = await request(path, { ...init, headers });
  return (await response.json()) as T;
}

export type DocumentKind = Document["kind"];

export interface UploadOptions {
  /** What the file is; a drawing set unless said otherwise (ADR-0014). */
  kind?: DocumentKind;
}

export function uploadDocument(file: File, options: UploadOptions = {}): Promise<Document> {
  const body = new FormData();
  body.append("file", file, file.name);
  if (options.kind) body.append("kind", options.kind);
  // No Content-Type: the browser writes the multipart boundary.
  return request("/documents", { method: "POST", body }).then(
    (response) => response.json() as Promise<Document>,
  );
}

export function getDocument(id: string): Promise<Document> {
  return json<Document>(`/documents/${id}`);
}

/** Every page of a document with its rasters and classified view regions. */
export function getPages(id: string): Promise<Pages> {
  return json<Pages>(`/documents/${id}/pages`);
}

/** Start a take-off run. 409 when one is already queued or running. */
export function createRun(documentId: string, body: Partial<RunCreate> = {}): Promise<Run> {
  return json<Run>(`/documents/${documentId}/runs`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function getRun(id: string): Promise<Run> {
  return json<Run>(`/runs/${id}`);
}

/** Every run of a document, newest first. */
export function listRuns(documentId: string): Promise<Run[]> {
  return json<Run[]>(`/documents/${documentId}/runs`);
}

export type Unit = components["schemas"]["UnitOut"];
export type Units = components["schemas"]["UnitsOut"];
export type UnitPatch = components["schemas"]["UnitPatch"];
export type InstanceCounts = components["schemas"]["InstanceCounts"];
export type Instance = components["schemas"]["InstanceOut"];
export type Instances = components["schemas"]["InstancesOut"];
export type Evidence = components["schemas"]["EvidenceOut"];
export type Decision = components["schemas"]["DecisionOut"];
export type ReviewCreate = components["schemas"]["ReviewCreate"];
export type ReviewDecision = ReviewCreate["decision"];
export type LinkedInstance = components["schemas"]["LinkedInstanceOut"];
export type UnitRun = components["schemas"]["UnitRunOut"];

/** Edit a unit. 409 when the mark or an alias is another unit's mark. */
export function patchUnit(unitId: string, body: UnitPatch): Promise<Unit> {
  return json<Unit>(`/units/${unitId}`, { method: "PATCH", body: JSON.stringify(body) });
}

/** Fold `unitId` into `intoUnitId`; returns the target with its new aliases and counts. */
export function mergeUnit(unitId: string, intoUnitId: string): Promise<Unit> {
  return json<Unit>(`/units/${unitId}/merge`, {
    method: "POST",
    body: JSON.stringify({ into_unit_id: intoUnitId }),
  });
}

export type SizeCount = components["schemas"]["SizeCount"];
export type DecisionFilter = "proposed" | "accepted" | "rejected" | "relabelled";

/** Which slice of a unit's instances to fetch. A unit of panels holds hundreds. */
export interface InstanceQuery {
  limit?: number;
  offset?: number;
  pageIndex?: number;
  floor?: string;
  decision?: DecisionFilter;
}

function queryString(query: InstanceQuery): string {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));
  if (query.pageIndex !== undefined) params.set("page_index", String(query.pageIndex));
  if (query.floor !== undefined) params.set("floor", query.floor);
  if (query.decision !== undefined) params.set("decision", query.decision);
  const text = params.toString();
  return text ? `?${text}` : "";
}

/** A unit's instances in reading order, a page at a time, with `total` for the rest. */
export function listUnitInstances(unitId: string, query: InstanceQuery = {}): Promise<Instances> {
  return json<Instances>(`/units/${unitId}/instances${queryString(query)}`);
}

/** The most the API returns per request. */
export const MAX_LIMIT = 500;

/**
 * A run's instances, or just one sheet's for the overlay. An elevation can hold more
 * panels than one response carries, and the overlay must draw them all, so this reads
 * on until it has every one.
 */
export async function listRunInstances(runId: string, pageIndex?: number): Promise<Instances> {
  const first = await json<Instances>(
    `/runs/${runId}/instances${queryString({ ...(pageIndex === undefined ? {} : { pageIndex }) })}`,
  );
  const instances = [...first.instances];
  while (instances.length < first.total) {
    const next = await json<Instances>(
      `/runs/${runId}/instances${queryString({
        ...(pageIndex === undefined ? {} : { pageIndex }),
        limit: MAX_LIMIT,
        offset: instances.length,
      })}`,
    );
    if (next.instances.length === 0) {
      break;
    }
    instances.push(...next.instances);
  }
  return { ...first, instances };
}

/** The take-off table of a run as a CSV download: a count per unit and size. */
export function takeoffCsvUrl(runId: string): string {
  return `${API_PREFIX}/runs/${runId}/takeoff.csv`;
}

/** Append the estimator's decision. Earlier decisions are kept; the newest counts. */
export function reviewInstance(instanceId: string, body: ReviewCreate): Promise<Instance> {
  return json<Instance>(`/instances/${instanceId}/review`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** One decision for several panels of a run, as when a block is boxed and relabelled
 * together: each panel gets its own decision, all or none. */
export async function reviewInstances(
  instanceIds: readonly string[],
  review: ReviewCreate,
): Promise<Instance[]> {
  const body = await json<{ instances: Instance[] }>("/instances/review", {
    method: "POST",
    body: JSON.stringify({ instance_ids: instanceIds, review }),
  });
  return body.instances;
}

/** Per-unit linking progress and cost for a run, in mark order. */
export function listUnitRuns(runId: string): Promise<UnitRun[]> {
  return json<UnitRun[]>(`/runs/${runId}/unit-runs`);
}

/* ---- The guided flow: choose sheets, run, take-off ------------------------------- */

export type DocumentPatch = components["schemas"]["DocumentPatch"];

/** Record who permitted model calls for a set, or restrict it again. The note is required. */
export function patchDocument(id: string, body: DocumentPatch): Promise<Document> {
  return json<Document>(`/documents/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export type DocumentListItem = components["schemas"]["DocumentListItem"];
export type DocumentList = components["schemas"]["DocumentListOut"];
export type LatestRun = components["schemas"]["LatestRunOut"];

export interface DocumentQuery {
  /** Part of a filename, any case. */
  q?: string;
  limit?: number;
  offset?: number;
  /** Drawing sets by default; `reference` lists every other kind. */
  kind?: DocumentKind | "reference";
}

/** One page of the organization's documents, newest first, each with its latest run. */
export function listDocuments(query: DocumentQuery = {}): Promise<DocumentList> {
  const params = new URLSearchParams();
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.kind) params.set("kind", query.kind);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));
  const text = params.toString();
  return json<DocumentList>(`/documents${text ? `?${text}` : ""}`);
}

export type AuditEvent = components["schemas"]["AuditEventOut"];
export type AuditPage = components["schemas"]["AuditPageOut"];

export interface AuditQuery {
  action?: string;
  actorUserId?: string;
  limit?: number;
  offset?: number;
}

/** One page of the organization's audit log, newest first. Owners and admins only. */
export function listAudit(query: AuditQuery = {}): Promise<AuditPage> {
  const params = new URLSearchParams();
  if (query.action) params.set("action", query.action);
  if (query.actorUserId) params.set("actor_user_id", query.actorUserId);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));
  const text = params.toString();
  return json<AuditPage>(`/audit${text ? `?${text}` : ""}`);
}

export type Me = components["schemas"]["MeOut"];
/** A role in an organization. Darkstar staff viewing a workspace show as `staff_viewer`,
 * which no member holds (ADR-0018). */
export type Role = Exclude<Me["role"], "staff_viewer">;
/** Darkstar staff, in the platform console: no organization (ADR-0015). */
export type StaffMe = components["schemas"]["StaffMeOut"];
/** Whoever is signed in; `kind` says which. */
export type Who = Me | StaffMe;

/** Who is signed in. A 401 means nobody: the caller shows sign-in. */
export function getMe(): Promise<Who> {
  return json<Who>("/auth/me");
}

/** Sign in; the API sets the session cookie. 401 for a wrong email or password, 429
 * after too many tries. */
export function login(email: string, password: string): Promise<Who> {
  return json<Who>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
}

export type AuthMethods = components["schemas"]["AuthMethodsOut"];

/** What the sign-in page offers besides email and password. */
export function getAuthMethods(): Promise<AuthMethods> {
  return json<AuthMethods>("/auth/methods");
}

/** Darkstar staff: sign in once with a break-glass link's token (ADR-0016). 401 when the
 * link is used, expired, or its staff member no longer is. */
export function staffLinkSignIn(token: string): Promise<StaffMe> {
  return json<StaffMe>("/auth/staff-link", { method: "POST", body: JSON.stringify({ token }) });
}

/** Where the browser goes to sign in with Google (ADR-0016): a page load, not a fetch,
 * since Google sends it back to the API, which redirects into the platform console. */
export function googleSignInUrl(next: string): string {
  return `/api/auth/google/start?next=${encodeURIComponent(next)}`;
}

export type PlatformOrg = components["schemas"]["PlatformOrgOut"];

/** Every organization, with its people and licence, newest first. Staff only. */
export async function listPlatformOrgs(): Promise<PlatformOrg[]> {
  const page = await json<components["schemas"]["PlatformOrgsOut"]>("/platform/orgs");
  return page.organizations;
}

export type PlatformOrgDetail = components["schemas"]["PlatformOrgDetailOut"];
export type PlatformOrgCreate = components["schemas"]["PlatformOrgIn"];

/** One organization: its licence, members and pending invites. Staff only. */
export function getPlatformOrg(id: string): Promise<PlatformOrgDetail> {
  return json<PlatformOrgDetail>(`/platform/orgs/${id}`);
}

/** Create an organization with its licence and mail its first owner an invite. 409 says
 * why it was refused: a taken slug, or an owner who cannot be invited. */
export function createPlatformOrg(body: PlatformOrgCreate): Promise<PlatformOrgDetail> {
  return json<PlatformOrgDetail>("/platform/orgs", { method: "POST", body: JSON.stringify(body) });
}

export type PlatformLicenceSet = components["schemas"]["PlatformLicenceSetIn"];

/** Replace the organization's licence. Suspended refuses everyone in it sign-in until it
 * is reactivated. */
export function setPlatformLicence(
  id: string,
  body: PlatformLicenceSet,
): Promise<PlatformOrgDetail> {
  return json<PlatformOrgDetail>(`/platform/orgs/${id}/licence`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Mail a pending invite a new link and expiry; the old link stops working. */
export function resendPlatformInvite(orgId: string, inviteId: string): Promise<Invite> {
  return json<Invite>(`/platform/orgs/${orgId}/invites/${inviteId}/resend`, { method: "POST" });
}

export async function revokePlatformInvite(orgId: string, inviteId: string): Promise<void> {
  await request(`/platform/orgs/${orgId}/invites/${inviteId}`, { method: "DELETE" });
}

/** Mail someone an invite to be an owner. 409 says why it was refused. */
export function invitePlatformOwner(id: string, email: string): Promise<Invite> {
  return json<Invite>(`/platform/orgs/${id}/owner-invite`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export type PlatformView = components["schemas"]["PlatformViewOut"];
export type PlatformReread = components["schemas"]["PlatformRereadOut"];

/** View the organization's workspace read-only, until `viewing_until` (ADR-0018). Ask
 * `getMe` after: this session now acts for that organization. Staff only. */
export function openStaffView(orgId: string): Promise<PlatformView> {
  return json<PlatformView>(`/platform/orgs/${orgId}/view`, { method: "POST" });
}

/** Infer the organization's code standard again now, and queue its past take-offs and
 * legends to be read again. Staff only; recorded for Darkstar. */
export function rereadOrgKnowledge(orgId: string): Promise<PlatformReread> {
  return json<PlatformReread>(`/platform/orgs/${orgId}/knowledge/reread`, { method: "POST" });
}

/** Leave the workspace being viewed, back to the platform console. */
export async function exitStaffView(): Promise<void> {
  await request("/platform/view", { method: "DELETE" });
}

export async function logout(): Promise<void> {
  await request("/auth/logout", { method: "POST" });
}

export type Organization = components["schemas"]["OrganizationDetailOut"];
export type Licence = components["schemas"]["LicenceOut"];

/** The organization, with its licence for a role that may read it. */
export function getOrganization(): Promise<Organization> {
  return json<Organization>("/organization");
}

export function renameOrganization(name: string): Promise<Organization> {
  return json<Organization>("/organization", { method: "PATCH", body: JSON.stringify({ name }) });
}

/** Change your own name; answers who you are now. */
export function updateMe(name: string): Promise<Me> {
  return json<Me>("/auth/me", { method: "PATCH", body: JSON.stringify({ name }) });
}

/** 403 when the current password is wrong; every other session of yours ends. */
export async function changePassword(current: string, next: string): Promise<void> {
  await request("/auth/password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ current_password: current, new_password: next }),
  });
}

export type Members = components["schemas"]["MembersOut"];
export type Member = components["schemas"]["MemberOut"];
export type Invite = components["schemas"]["InviteOut"];
export type InviteLookup = components["schemas"]["InviteLookupOut"];

/** Members, pending invites and seats. Owners and admins only. */
export function listMembers(): Promise<Members> {
  return json<Members>("/members");
}

/** Invite by email; the invite is mailed. 409 says why it cannot be made. */
export function inviteMember(email: string, role: Role): Promise<Invite> {
  return json<Invite>("/members/invites", {
    method: "POST",
    body: JSON.stringify({ email, role }),
  });
}

/** A new link and a new expiry; the old link stops working. */
export function resendInvite(id: string): Promise<Invite> {
  return json<Invite>(`/members/invites/${id}/resend`, { method: "POST" });
}

export async function revokeInvite(id: string): Promise<void> {
  await request(`/members/invites/${id}`, { method: "DELETE" });
}

export type MemberPatch = components["schemas"]["MemberPatch"];

/** Change a member's role or status. 409 for the last owner; deactivating signs them out. */
export function updateMember(userId: string, change: MemberPatch): Promise<Member> {
  return json<Member>(`/members/${userId}`, { method: "PATCH", body: JSON.stringify(change) });
}

/** Always answers the same; the link is mailed only to an active account. */
export async function forgotPassword(email: string): Promise<void> {
  await json<unknown>("/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/** Set a new password from a reset link. 404 when the link is used or expired. */
export async function resetPassword(token: string, password: string): Promise<void> {
  await request("/auth/password/reset", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
}

/** What an invite link is for. 404 when it is used, revoked, expired or unknown. */
export function lookupInvite(token: string): Promise<InviteLookup> {
  return json<InviteLookup>("/auth/invites/lookup", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

/** Accept with a name and password; the API signs the new member in. */
export function acceptInvite(token: string, name: string, password: string): Promise<Me> {
  return json<Me>("/auth/invites/accept", {
    method: "POST",
    body: JSON.stringify({ token, name, password }),
  });
}

export type Extraction = components["schemas"]["ExtractionOut"];
export type Taught = components["schemas"]["TaughtOut"];

/** What was read from a past take-off and what it taught. */
export function getExtraction(documentId: string): Promise<Extraction> {
  return json<Extraction>(`/documents/${documentId}/extraction`);
}

/** Read a past take-off again, and learn from it again. */
export async function rereadSource(documentId: string): Promise<void> {
  await request(`/documents/${documentId}/extraction`, { method: "POST" });
}

export type CodeStandard = components["schemas"]["CodeStandardOut"];
export type CodeStandardDef = components["schemas"]["CodeStandardDef"];
export type Segment = components["schemas"]["SegmentDef"];
export type CodeParts = components["schemas"]["CodePartsOut"];

/** The organization's code standard and how its past codes decode under it. */
export function getCodeStandard(): Promise<CodeStandard> {
  return json<CodeStandard>("/knowledge/code-standard");
}

/** Save the standard; saving confirms it, so nothing inferred replaces it after. 422 for
 * a definition that could not decode a code. */
export function saveCodeStandard(definition: CodeStandardDef): Promise<CodeStandard> {
  return json<CodeStandard>("/knowledge/code-standard", {
    method: "PUT",
    body: JSON.stringify({ definition }),
  });
}

/** Back to proposed, inferred again from past take-offs and the legend. */
export function reinferCodeStandard(): Promise<CodeStandard> {
  return json<CodeStandard>("/knowledge/code-standard/reinfer", { method: "POST" });
}

export type KnowledgeScore = components["schemas"]["KnowledgeScoreOut"];
export type PartScore = components["schemas"]["PartScoreOut"];

/** How well the library predicts each past take-off without its own votes. */
export function getKnowledgeScore(): Promise<KnowledgeScore> {
  return json<KnowledgeScore>("/knowledge/score");
}

export type LibraryEntry = components["schemas"]["LibraryEntryOut"];
export type Library = components["schemas"]["LibraryOut"];

/** What the type library has learned, most recently taught first. */
export function listLibrary(limit = 500): Promise<Library> {
  return json<Library>(`/library?limit=${limit}`);
}

/** Remove a set and everything derived from it. 409 while a run is active. */
export async function deleteDocument(id: string): Promise<void> {
  await request(`/documents/${id}`, { method: "DELETE" });
}

export type Sheet = components["schemas"]["SheetOut"];
export type SheetRole = Sheet["role"];
export type Sheets = components["schemas"]["SheetsOut"];
export type SheetsConfirm = components["schemas"]["SheetsConfirm"];

export function getSheets(documentId: string): Promise<Sheets> {
  return json<Sheets>(`/documents/${documentId}/sheets`);
}

export function putSheets(documentId: string, selected: number[]): Promise<Sheets> {
  return json<Sheets>(`/documents/${documentId}/sheets`, {
    method: "PUT",
    body: JSON.stringify({ selected }),
  });
}

/** Confirm the selection and start the run on it. 403 restricted, 409 active, 422 empty. */
export function confirmSheets(documentId: string, body: SheetsConfirm = {}): Promise<Run> {
  return json<Run>(`/documents/${documentId}/sheets/confirm`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type TakeoffSheet = components["schemas"]["TakeoffSheetOut"];
export type TakeoffSize = components["schemas"]["TakeoffSizeOut"];
/** `colour` is `#rrggbb`, the same in the legend, the overlay and the PDF. */
export type TakeoffConfiguration = components["schemas"]["TakeoffConfigurationOut"];
export type Takeoff = components["schemas"]["TakeoffOut"];

export function getTakeoff(runId: string): Promise<Takeoff> {
  return json<Takeoff>(`/runs/${runId}/takeoff`);
}

/** The marked-up sheets as one PDF; `inline` opens it in the browser instead of saving. */
export function takeoffPdfUrl(runId: string, inline = false): string {
  return `${API_PREFIX}/runs/${runId}/takeoff.pdf${inline ? "?inline=1" : ""}`;
}

/* ---- One type at a time: its notes, details, shapes and reference panel ---------- */

export type TypeDetails = components["schemas"]["TypeDetails"];
/** Points are page-normalised (0..1, origin top-left), on the page `page_id`. */
export type Shape = components["schemas"]["Shape"];
export type UnitAnnotation = components["schemas"]["UnitAnnotationOut"];
export type UnitAnnotationPut = components["schemas"]["UnitAnnotationPut"];
export type UnitReference = components["schemas"]["UnitReferenceOut"];
export type LearnedFrom = components["schemas"]["LearnedFromOut"];

/** A unit's notes, details and shapes; an empty one if nothing was saved. 409 if merged. */
export function getUnitAnnotation(unitId: string): Promise<UnitAnnotation> {
  return json<UnitAnnotation>(`/units/${unitId}/annotation`);
}

/** Replace the whole annotation: what is left out is cleared. */
export function putUnitAnnotation(
  unitId: string,
  body: UnitAnnotationPut,
): Promise<UnitAnnotation> {
  return json<UnitAnnotation>(`/units/${unitId}/annotation`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** The panel that stands for a type, and the page window its crop shows. */
export function getUnitReference(unitId: string): Promise<UnitReference> {
  return json<UnitReference>(`/units/${unitId}/reference`);
}

// ---------------------------------------------------------------- the workspace (ADR-0020)

export type WorkspaceSheet = components["schemas"]["WorkspaceSheetOut"];
export type Workspace = components["schemas"]["WorkspaceOut"];
export type WorkspaceConfig = components["schemas"]["WorkspaceConfigOut"];
export type WorkspacePane = components["schemas"]["WorkspacePaneOut"];
export type WorkspaceUnit = components["schemas"]["WorkspaceUnitOut"];
export type WorkspaceFlag = components["schemas"]["WorkspaceFlagOut"];
/** One typed operation on a sheet's workspace: the same set an agent will use. */
export type WorkspaceOp = components["schemas"]["OpsIn"]["ops"][number];

/** The sheets of a run that have a workspace, with how far review has got. */
export async function listWorkspaceSheets(runId: string): Promise<WorkspaceSheet[]> {
  const body = await json<components["schemas"]["WorkspaceSheetsOut"]>(
    `/runs/${runId}/workspace-sheets`,
  );
  return body.sheets;
}

/** A sheet's configurations, panes, units, names and flags. */
export function getWorkspace(runId: string, pageIndex: number): Promise<Workspace> {
  return json<Workspace>(`/runs/${runId}/sheets/${pageIndex}/workspace`);
}

/** Apply operations in order, all or none; returns the sheet as it now is. */
export function applyOps(runId: string, pageIndex: number, ops: WorkspaceOp[]): Promise<Workspace> {
  return json<Workspace>(`/runs/${runId}/sheets/${pageIndex}/ops`, {
    method: "POST",
    body: JSON.stringify({ ops }),
  });
}
